import { useNavigate } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { useMaterialRequestStore } from '../../../../../store/MaterialRequestStore';
import { useAuthStore } from '../../../../../store';
import React, { useEffect, useState } from 'react';
import {
  MRS_API,
  useUploadMRFiles,
} from '../../../../../hooks/useMaterialRequestHooks';
import { useCreateData, useFetchData } from '../../../../../hooks';
import { MaterialRequestRecord } from '../../../../../types/MaterialRequest';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import { Resource } from '../../../../../lib/enums';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import CustomForm from '../../../../../components/ui/CustomForm';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import MaterialRequestMovementCreate from './MaterialRequestMovementCreate';

const MaterialRequestFormCreatePage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const materialRequestStore = useMaterialRequestStore();
  const { user } = useAuthStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  const [fileList, setFileList] = useState<FormData | null>(null);

  const {
    mutate: mutateFile,
    isError: isFileError,
    error: fileError,
  } = useUploadMRFiles();
  const {
    mutate,
    isPending,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
    data: createdData,
  } = useCreateData(MRS_API);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/material-request');
  };

  const handleClose = () => {
    navigate('/stock-movement/material-request');
  };

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  useEffect(() => {
    if (isSuccessCreate) {
      if (fileList !== null) {
        mutateFile({ id: createdData?.data.id, formData: fileList });
      }
      setIsInfoModalOpen(true);
    }
    if (isErrorCreate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorCreate?.response?.data.message);
    }
    if (isFileError) {
      setIsErrorModalOpen(true);
      setErrorMess(fileError?.message);
    }
  }, [isSuccessCreate, isErrorCreate, isFileError]);

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const location: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.location;
      },
    );

    const payload: MaterialRequestRecord = {
      ...values,
      destination: destination,
      location: location,
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      movements: materialRequestStore.formData.movements || [],
    };
    mutate(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      setFileList(formData);
    }
  };

  const fields: FormField[] = [
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: materialRequestStore.formData.requiredDate,
    },
    {
      name: 'location',
      label: 'Location',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select location' }],
      defaultValue: materialRequestStore.formData.destination,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue:
        materialRequestStore.formData.destination != null
          ? materialRequestStore.formData.destination.id
          : user?.defaultLocation?.id,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: materialRequestStore.formData.source,
    },
    {
      name: 'notes',
      label: 'Remarks',
      type: 'textarea',
      defaultValue: materialRequestStore.formData.source,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: materialRequestStore.formData.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Material request'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        <div className="p-4">
          <CustomForm
            data={materialRequestStore.formData}
            formFields={fields}
            form={form}
            onFinish={onFinish}
            rowCol={3}
            layout={'vertical'}
            formName={'po_form'}
          />
        </div>
        <MaterialRequestMovementCreate />
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Material Request successfully created'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default MaterialRequestFormCreatePage;
